import * as THREE from 'three';
import { createCar } from '../game/Car';
import { MAP } from '../utils/constants';

/**
 * Lightweight lobby loop: two cars on a dark slab — “live gameplay” tease without
 * loading the full city.
 */
export class LobbyPreview {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private raf = 0;
  private disposed = false;
  private t = 0;

  constructor(mount: HTMLElement | null) {
    if (!mount) return;
    const w = Math.min(560, Math.max(280, window.innerWidth - 40));
    const h = 200;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'low-power',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x070b12, 1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);
    this.renderer = renderer;

    const scene = new THREE.Scene();
    this.scene = scene;
    scene.fog = new THREE.Fog(0x070b12, 120, 420);

    const cam = new THREE.PerspectiveCamera(38, w / h, 0.1, 800);
    cam.position.set(0, 95, 155);
    cam.lookAt(0, 0, 0);

    scene.add(new THREE.AmbientLight(0xaaccff, 0.45));
    const sun = new THREE.DirectionalLight(0xfff0e8, 0.95);
    sun.position.set(60, 120, 40);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    scene.add(sun);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(MAP.SIZE * 0.45, MAP.SIZE * 0.45),
      new THREE.MeshLambertMaterial({ color: 0x141a24 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(52, 58, 64),
      new THREE.MeshLambertMaterial({
        color: 0x2a3548,
        transparent: true,
        opacity: 0.85,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.04;
    ring.receiveShadow = true;
    scene.add(ring);

    const driver = createCar('driver');
    driver.scale.setScalar(1.15);
    scene.add(driver);

    const cop = createCar('cop');
    cop.scale.setScalar(1.15);
    scene.add(cop);

    const loop = (): void => {
      if (this.disposed) return;
      this.raf = requestAnimationFrame(loop);
      this.t += 0.018;
      const a = this.t * 0.42;
      const r = 48;
      driver.position.set(Math.sin(a) * r, 0, Math.cos(a) * r);
      driver.rotation.y = a + Math.PI * 0.5;
      cop.position.set(
        Math.sin(a + 1.1) * (r - 14),
        0,
        Math.cos(a + 1.1) * (r - 14),
      );
      cop.rotation.y = a + 1.1 + Math.PI * 0.5;
      renderer.render(scene, cam);
    };
    loop();
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    if (this.scene) {
      this.scene.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          const m = o.material;
          if (Array.isArray(m)) m.forEach((x) => x.dispose());
          else m.dispose();
        }
      });
    }
    this.scene = null;
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.domElement.remove();
    }
    this.renderer = null;
  }
}
